import { useState, useEffect, useCallback } from 'react'
import { showToast } from '@/components/layout/Toast'
import { IconRefresh, IconHeadset } from '@/components/icons/Icons'
import FlujoDelAgente from './FlujoDelAgente'

/**
 * La pantalla de un bot del frontdesk: qué hace, su flujo, y qué fue recogiendo.
 *
 * Sirve para los dos (soporte y pedidos) porque lo único que cambia es el texto de arriba
 * y qué columnas tiene sentido mostrar. Lo que NO cambia es lo importante: el editor de
 * flujo, el chat de prueba y la tabla de lo recogido.
 *
 * El texto de arriba no es decoración: estos bots tienen guardas distintas entre sí y
 * opuestas a las del recepcionista, y confundirlos es escribirle al grupo equivocado. Por
 * eso cada uno dice, en su propia pantalla, a quién le habla y a quién no.
 *
 * El interruptor se muestra pero **no se toca desde acá**: es una variable del contenedor,
 * y un botón que prometiera apagarlo mentiría.
 */

const API_URL = import.meta.env.VITE_API_URL || 'https://api.1conecta.com'

interface Sesion {
  id: number
  conversacion_id: number
  paso: string
  estado: string
  datos: Record<string, any>
  mensajes_enviados: number
  motivo_entrega: string | null
  tarea_id: number | null
  updated_at: string
}

interface Props {
  slug: string
  titulo: string
  /** Qué hace y a quién le habla, en dos o tres frases. */
  quePasa: React.ReactNode
  /** Qué dice el editor de flujo arriba. */
  descripcionFlujo: React.ReactNode
  /** Los datos que este bot recoge, para la columna de chips. Vacío = no se muestra. */
  datos?: { clave: string; titulo: string }[]
  /** De qué columna sale el resumen de cada fila. */
  claveResumen: string
  vacio: string
}

export default function BotDelFrontdesk({
  slug, titulo, quePasa, descripcionFlujo, datos = [], claveResumen, vacio,
}: Props) {
  const [sesiones, setSesiones] = useState<Sesion[]>([])
  const [cargando, setCargando] = useState(true)

  const token = () => localStorage.getItem('auth_token')

  const cargar = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/chat/frontdesk/sesiones?agente=${slug}`, {
        headers: { Authorization: `Bearer ${token()}` },
      })
      if (res.ok) {
        const d = await res.json()
        setSesiones(d.items ?? [])
      }
    } catch {
      showToast('No se pudo cargar lo que recogió el bot', 'error')
    } finally {
      setCargando(false)
    }
  }, [slug])

  useEffect(() => { void cargar() }, [cargar])

  if (cargando) {
    return <div className="card"><div style={{ padding: 24, color: 'var(--text-secondary)' }}>Cargando…</div></div>
  }

  const activas = sesiones.filter(s => s.estado === 'activa')

  return (
    <div>
      <div className="card" style={{ marginBottom: 24 }}>
        <div className="card-header">
          <h2 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <IconHeadset size={18} /> {titulo}
          </h2>
        </div>

        {quePasa}
      </div>

      <FlujoDelAgente slug={slug} leerTodo descripcion={descripcionFlujo} />

      <div className="card">
        <div className="card-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2>Lo que fue recogiendo</h2>
          <button className="btn btn-sm btn-secondary" onClick={() => void cargar()} type="button">
            <IconRefresh size={13} /> Actualizar
          </button>
        </div>

        {sesiones.length === 0 ? (
          <p style={{ color: 'var(--text-secondary)', fontSize: '0.86rem' }}>
            {vacio}
          </p>
        ) : (
          <>
            <p style={{ color: 'var(--text-secondary)', fontSize: '0.82rem', marginTop: 0 }}>
              {activas.length} en curso · {sesiones.length} en total
            </p>
            <div className="table-container">
              <table className="table">
                <thead>
                  <tr>
                    <th>Qué dijo</th>
                    {datos.length > 0 && <th>Datos</th>}
                    <th>Estado</th>
                    <th>Tarea</th>
                  </tr>
                </thead>
                <tbody>
                  {sesiones.map(s => (
                    <tr key={s.id}>
                      <td>{s.datos?.[claveResumen] || '—'}</td>
                      {/* Qué datos tiene y qué le falta: es lo que dice si el bot está
                          trabado esperando algo que el cliente no va a mandar. El bot de
                          pedidos no recoge datos, así que ni la columna aparece. */}
                      {datos.length > 0 && (
                      <td>
                        {datos.map(d => (
                          <span
                            key={d.clave}
                            className={`badge badge-${s.datos?.[d.clave] ? 'success' : 'neutral'}`}
                            style={{ marginRight: 4 }}
                          >
                            {d.titulo}
                          </span>
                        ))}
                      </td>
                      )}
                      <td>
                        <span className={`badge badge-${s.estado === 'activa' ? 'info' : s.estado === 'completada' ? 'success' : 'warning'}`}>
                          {s.estado}
                        </span>
                        {s.motivo_entrega && (
                          <span style={{ color: 'var(--text-muted)', fontSize: '0.78rem' }}> — {s.motivo_entrega}</span>
                        )}
                      </td>
                      <td>
                        {s.tarea_id
                          ? <span className="badge badge-success">#{s.tarea_id}</span>
                          : <span style={{ color: 'var(--text-muted)' }}>—</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
